import { Alert, Box, Button, Dialog, DialogActions, DialogContent, styled, Typography } from '@mui/material';
import React, { useCallback, useMemo } from 'react';

import { TEMPLATE_PAGE_NAMES } from '../../../constants/templates';
import { IClaimLinkTemplateErrors } from '../../../types/templates';
import { getCountLabel } from '../../../utils/helpers';
import { parseTemplateErrors } from '../../../utils/templates';
import { DialogCloseButton } from '../_controls_';

interface IProps {
  open: boolean;
  onClose: () => void;
  errors?: IClaimLinkTemplateErrors | null;
}

const StyledDialog = styled(Dialog)(() => ({
  '& .MuiDialog-paper': {
    borderRadius: 4,
    minWidth: 560,
    maxWidth: 600,
  },
}));

const StyledDialogContent = styled(DialogContent)(() => ({
  maxHeight: 500,
  overflowY: 'auto',
  padding: '0 24px',
}));

export const TemplateBuilderErrorModal: React.FC<IProps> = ({ open, onClose, errors }) => {
  // Parse and group errors by page using utility function
  const { pageErrors, totalIssues, nonPageErrors } = useMemo(
    () => parseTemplateErrors(errors, TEMPLATE_PAGE_NAMES),
    [errors],
  );

  const renderErrors = useCallback((index: number, error: string) => {
    return (
      <Typography
        key={index}
        component="li"
        variant="body2"
        sx={{
          display: 'list-item',
        }}
      >
        {error}
      </Typography>
    );
  }, []);

  const totalPages = pageErrors.length + (nonPageErrors.length > 0 ? 1 : 0);

  if (totalIssues === 0) {
    return null;
  }

  return (
    <StyledDialog open={open} onClose={onClose}>
      <Box sx={{ pb: 0, pt: 3, px: 3, position: 'relative' }}>
        <DialogCloseButton onClick={onClose} dataAction="close_error_modal" />
        <Typography
          variant="h6"
          fontWeight="medium"
          sx={{
            mb: 2,
            pr: 5,
          }}
        >
          Can't publish this template yet
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Fix the highlighted fields to publish
        </Typography>
      </Box>

      <Box sx={{ px: 3, pt: 2, pb: 1 }}>
        <Typography variant="body2" fontWeight="medium">
          {getCountLabel(totalIssues, 'issue', 'issues')} in {getCountLabel(totalPages, 'page', 'pages')}
        </Typography>
      </Box>

      <StyledDialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}>
          {pageErrors.map((page, index) => (
            <Alert key={index} severity="error" sx={{ alignItems: 'flex-start' }}>
              <Typography
                variant="body1"
                fontWeight="medium"
                sx={{
                  mb: page.errors.length > 0 ? 1 : 0,
                }}
              >
                {page.pageName} ({page.errors.length})
              </Typography>
              {page.errors.length > 0 && (
                <Box
                  component="ul"
                  sx={{
                    margin: 0,
                    paddingLeft: 2.5,
                    listStyleType: 'disc',
                    listStylePosition: 'outside',
                  }}
                >
                  {page.errors.map((error, i) => renderErrors(i, error))}
                </Box>
              )}
            </Alert>
          ))}

          {nonPageErrors.length > 0 && (
            <Alert severity="error" sx={{ alignItems: 'flex-start' }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Template Settings ({nonPageErrors.length})
              </Typography>
              <Box
                component="ul"
                sx={{
                  margin: 0,
                  paddingLeft: 2.5,
                  listStyleType: 'disc',
                  listStylePosition: 'outside',
                }}
              >
                {nonPageErrors.map((err, i) => renderErrors(i, err))}
              </Box>
            </Alert>
          )}
        </Box>
      </StyledDialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 2, justifyContent: 'flex-end' }}>
        <Button onClick={onClose} variant="contained" sx={{ minWidth: 160 }}>
          Continue editing
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};
